import { Box, Button, Flex, Grid, Heading, HStack, Icon, Image, Text, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { useRef } from "react";
import { FiArrowUpRight, FiCheck } from "react-icons/fi";
import { portfolio, type ProjectItem } from "../data/portfolio";
import { AnimatedCounter } from "./AnimatedCounter";
import { PublishedApps } from "./AppsShowcase";
import { MotionFade } from "./MotionFade";
import { Accent, SectionHeading } from "./SectionHeading";

const MotionBox = motion(Box);

const hostOf = (url: string) => new URL(url).host;

function BrowserFrame({ project }: { project: ProjectItem }) {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  // The screenshot starts tilted back and settles flat as it reaches the middle of the viewport.
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.95", "center 0.55"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 80, damping: 20, mass: 0.5 });
  const rotateX = useTransform(smooth, [0, 1], [22, 0]);
  const scale = useTransform(smooth, [0, 1], [0.88, 1]);
  const glowOpacity = useTransform(smooth, [0, 1], [0, 0.26]);

  return (
    <Box ref={ref} position="relative" sx={{ perspective: "1400px" }}>
      <MotionBox
        position="absolute"
        inset="8%"
        borderRadius="full"
        bg="accent.500"
        filter="blur(90px)"
        style={{ opacity: prefersReducedMotion ? 0.26 : glowOpacity }}
        aria-hidden
      />
      <MotionBox
        position="relative"
        borderRadius={{ base: "xl", md: "2xl" }}
        overflow="hidden"
        borderWidth="1px"
        borderColor="whiteAlpha.300"
        bg="graphite.900"
        boxShadow="0 40px 100px rgba(0,0,0,0.6)"
        style={prefersReducedMotion ? undefined : { rotateX, scale, transformOrigin: "center bottom" }}
      >
        <HStack spacing={2} px={4} py={3} borderBottomWidth="1px" borderColor="whiteAlpha.200">
          {[0, 1, 2].map((i) => (
            <Box key={i} w="10px" h="10px" borderRadius="full" bg="whiteAlpha.300" />
          ))}
          {project.liveUrl ? (
            <Text
              ml={3}
              px={3}
              py={1}
              borderRadius="full"
              bg="whiteAlpha.100"
              fontFamily="mono"
              fontSize="11px"
              color="graphite.300"
              noOfLines={1}
            >
              {hostOf(project.liveUrl)}
            </Text>
          ) : null}
        </HStack>
        <Image
          src={project.image}
          alt={`Captura de tela do projeto ${project.title}`}
          w="full"
          display="block"
          style={{ aspectRatio: "16 / 9" }}
          objectFit="cover"
          objectPosition="top"
        />
      </MotionBox>
    </Box>
  );
}

function ProjectShowcase({ project, flipped }: { project: ProjectItem; flipped: boolean }) {
  return (
    <Grid
      templateColumns={{
        base: "1fr",
        lg: flipped ? "minmax(0, 5fr) minmax(0, 7fr)" : "minmax(0, 7fr) minmax(0, 5fr)",
      }}
      gap={{ base: 10, lg: 14 }}
      alignItems="center"
    >
      {project.image ? (
        <Box order={{ lg: flipped ? 2 : 1 }}>
          <BrowserFrame project={project} />
        </Box>
      ) : null}

      <Box order={{ lg: flipped ? 1 : 2 }}>
        <MotionFade>
          {project.context ? (
            <Text fontFamily="mono" fontSize="xs" letterSpacing="0.14em" textTransform="uppercase" color="accent.300" mb={3}>
              {project.context}
            </Text>
          ) : null}
          <Heading as="h3" fontSize={{ base: "3xl", md: "4xl" }} letterSpacing="-0.03em" color="white">
            {project.title}
          </Heading>
          <Text mt={4} lineHeight="tall" color="graphite.300">
            {project.description}
          </Text>
        </MotionFade>

        {project.metric ? (
          <MotionFade delay={0.06}>
            <HStack spacing={4} mt={7} align="baseline">
              <AnimatedCounter
                value={project.metric.value}
                prefix={project.metric.prefix}
                suffix={project.metric.suffix}
                fontFamily="heading"
                fontWeight="700"
                fontSize="5xl"
                lineHeight="1"
                letterSpacing="-0.04em"
                color="accent.300"
              />
              <Text fontFamily="mono" fontSize="xs" letterSpacing="0.1em" textTransform="uppercase" color="graphite.400">
                {project.metric.label}
              </Text>
            </HStack>
          </MotionFade>
        ) : null}

        {project.features ? (
          <VStack as="ul" align="stretch" spacing={0} mt={7} listStyleType="none">
            {project.features.map((feature, i) => (
              <MotionFade as="li" key={feature} delay={0.08 + i * 0.06}>
                <Flex align="center" gap={3} py={3} borderTopWidth="1px" borderColor="whiteAlpha.100" color="graphite.100">
                  <Icon as={FiCheck} color="accent.400" />
                  {feature}
                </Flex>
              </MotionFade>
            ))}
          </VStack>
        ) : null}

        <MotionFade delay={0.2}>
          <Wrap spacing={2} mt={6}>
            {project.tags.map((tag) => (
              <WrapItem key={tag}>
                <Text px={3} py={1} borderRadius="full" bg="whiteAlpha.100" fontFamily="mono" fontSize="11px" color="graphite.300">
                  {tag}
                </Text>
              </WrapItem>
            ))}
          </Wrap>

          <HStack spacing={3} mt={8} flexWrap="wrap">
            {project.liveUrl ? (
              <Button
                as="a"
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                colorScheme="accent"
                size="lg"
                borderRadius="full"
                rightIcon={<FiArrowUpRight />}
              >
                Ver ao vivo
              </Button>
            ) : null}
            {project.repoUrl ? (
              <Button
                as="a"
                href={project.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outline"
                size="lg"
                borderRadius="full"
                borderColor="whiteAlpha.400"
                rightIcon={<FiArrowUpRight />}
              >
                Repositório
              </Button>
            ) : null}
          </HStack>
        </MotionFade>
      </Box>
    </Grid>
  );
}

export function ProjectsSection() {
  return (
    <Box id="projetos" as="section" py={{ base: 20, md: 32 }} px={{ base: 5, md: 8 }} bg="#101014" scrollMarginTop="72px" overflow="hidden">
      <Box maxW="container.xl" mx="auto">
        <SectionHeading
          section="projetos"
          title={
            <>
              No ar, <Accent>para qualquer um abrir</Accent>
            </>
          }
          lead="Um produto próprio, um trabalho freelance e apps publicados nas lojas, todos em produção e com gente de verdade usando."
        />

        <VStack align="stretch" spacing={{ base: 20, md: 28 }}>
          {portfolio.projects.map((project, i) => (
            <ProjectShowcase key={project.title} project={project} flipped={i % 2 === 1} />
          ))}
        </VStack>

        {portfolio.publishedApps.length > 0 ? (
          <Box mt={{ base: 20, md: 28 }} pt={{ base: 10, md: 14 }} borderTopWidth="1px" borderColor="whiteAlpha.200">
            <Grid templateColumns={{ base: "1fr", lg: "minmax(0, 4fr) minmax(0, 8fr)" }} gap={{ base: 8, lg: 14 }} alignItems="start">
              <MotionFade>
                <Text fontFamily="mono" fontSize="xs" letterSpacing="0.14em" textTransform="uppercase" color="accent.300" mb={3}>
                  Nas lojas
                </Text>
                <Heading as="h3" fontSize={{ base: "2xl", md: "3xl" }} letterSpacing="-0.03em" color="white">
                  Apps publicados em que atuei
                </Heading>
                <Text mt={3} lineHeight="tall" color="graphite.300">
                  Aplicativos financeiros disponíveis no Google Play nos quais participei do desenvolvimento.
                </Text>
              </MotionFade>
              <PublishedApps apps={portfolio.publishedApps} />
            </Grid>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
